import { authStateAtom, dealershipAtom, isCognitoAuth } from '~/state';
import {
  FetchFunction,
  GraphQLResponse,
  Thunder,
  ZeusScalars
} from '../__generated__/backend/zeus';
import config from '../config';
import { getJwt } from './auth';
import store from './store';

export const scalars = ZeusScalars({
  JSON: {
    // This does not work due to Zeus and the way they escape
    encode: (e: unknown) => JSON.stringify(e),
    decode: (e: unknown) =>
      // The JSON fields in a response can be sent as objects and not strings.
      // Therefore, we have to check the type before trying to parse a JSON string.
      // If it's not a string, we are just returning the object
      typeof e === 'string' ? JSON.parse(e) : e
  },
  DateTime: {
    decode: (e: unknown) => new Date(String(e)),
    // It inserts it verbatim, not with enclosing `"` for strings
    encode: (e: unknown) => {
      // I don't know why this happens, and I also was not interested in spending days getting into this.
      // It appears that the function is called at weird times.
      if (!e) {
        console.log('Encoding null DateTime', e);
      }
      return `"${e instanceof Date ? e.toISOString() : String(e)}"`;
    }
  },
  BigInt: {
    decode: (e: unknown) => BigInt(String(e)),
    encode: (e: unknown) => String(e)
  }
});

type Options = {
  throwOnError?: boolean;
  dealershipId?: string | null;
  dealershipGroupId?: string | null;
};

const handleFetchResponse = async (
  response: Response
): Promise<GraphQLResponse> => {
  if (!response.ok) {
    const msg = await response.text();
    throw new Error(msg);
  }
  return await response.json();
};
/**
 * Build authorization headers using the current Jotai auth & dealership atom and cognito if set.
 * @param options Optional parameters for building headers.
 * @returns An object containing the authorization headers.
 */
export const buildAuthHeaders = async (options?: Options) => {
  const authJwt = store.instance.get(authStateAtom);
  const authHeaders: HeadersInit = {
    Application: 'dealership-dashboard'
  };

  const jwt = isCognitoAuth(authJwt) ? await getJwt() : authJwt?.jwt;

  const activeDs = store.instance.get(dealershipAtom)?.activeDealershipPerms;

  // Needed a mechanism to overwrite this.
  if (activeDs?.dealershipId && options?.dealershipId !== null) {
    authHeaders['DealershipId'] = activeDs?.dealershipId;
  }

  // Set from store
  if (activeDs?.dealershipGroupId && options?.dealershipGroupId !== null) {
    authHeaders['DealershipGroupId'] = activeDs?.dealershipGroupId;
  }

  // This one overwrites and takes explicit precedence
  if (options?.dealershipId) {
    authHeaders['DealershipId'] = options?.dealershipId;
  }

  // This one overwrites and takes explicit precedence
  if (options?.dealershipGroupId) {
    authHeaders['DealershipGroupId'] = options.dealershipGroupId;
  }

  if (jwt) {
    authHeaders['Authorization'] = `Bearer ${jwt}`;
  }
  return authHeaders;
};

const fetchFunction: (options?: Options) => FetchFunction =
  (options) =>
  async (query, variables: Record<string, unknown> = {}) => {
    const response = await fetch(config.backendUrl, {
      body: JSON.stringify(
        {
          query,
          variables: variables
        },
        (_, value) => {
          return value;
        }
      ),
      method: 'POST',
      headers: {
        ...(await buildAuthHeaders(options)),
        'Content-Type': 'application/json'
      }
    });

    const gqlResp = await handleFetchResponse(response);

    if (gqlResp.errors) {
      if (options?.throwOnError) {
        throw new Error(`${JSON.stringify(gqlResp.errors)} for ${query}`);
      } else {
        console.error(gqlResp.errors);
      }
    }

    return gqlResp.data;
  };

export const gqlQueryClient = (options?: Options) =>
  Thunder(fetchFunction(options), {
    scalars
  })('query');

export const gqlMutationClient = (options?: Options) =>
  Thunder(fetchFunction(options), {
    scalars
  })('mutation');
